import { useEffect, useState } from "react";
import { fontCssFamily, type FontFaceDescriptor } from "@proof/core";
import { loadBrowserFonts } from "@proof/fonts/browser";
import { useAuthoringServices } from "./services";

function FaceSample({ face }: { face: FontFaceDescriptor }) {
  const { loadFont } = useAuthoringServices();
  const [state, setState] = useState("loading");
  useEffect(() => {
    const controller = new AbortController();
    let dispose: (() => void) | undefined;
    setState("loading");
    if (loadFont)
      void loadBrowserFonts([face], loadFont, controller.signal)
        .then((fonts) => {
          if (controller.signal.aborted) fonts.dispose();
          else {
            dispose = fonts.dispose;
            setState("ready");
          }
        })
        .catch(() => {
          if (!controller.signal.aborted) setState("unavailable");
        });
    else setState("unavailable");
    return () => {
      controller.abort();
      dispose?.();
    };
  }, [face.id, loadFont]);
  return (
    <span
      style={
        state === "ready"
          ? {
              fontFamily: fontCssFamily(face),
              fontWeight: face.weight,
              fontStyle: face.style,
              fontSynthesis: "none",
            }
          : undefined
      }
    >
      {face.name}{" "}
      <small>
        {face.weight} {face.style}
        {state === "loading"
          ? " · loading sample"
          : state === "unavailable"
            ? " · sample unavailable"
            : ""}
      </small>
    </span>
  );
}

/** A real list of samples: native select menus do not reliably render webfonts on phones. */
export function FontPicker({
  fonts,
  label = "Managed font face",
  value,
  onChange,
}: {
  fonts: Map<string, FontFaceDescriptor>;
  label?: string;
  value?: string;
  onChange(face: FontFaceDescriptor | null): void;
}) {
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(""),
    [page, setPage] = useState(0);
  const faces = [...fonts.values()]
    .filter((face) =>
      `${face.name} ${face.family}`.toLowerCase().includes(query.toLowerCase()),
    )
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, "en", { sensitivity: "base" }) ||
        a.weight - b.weight ||
        a.style.localeCompare(b.style),
    );
  const current = Math.min(page, Math.max(0, Math.ceil(faces.length / 12) - 1));
  const choose = (face: FontFaceDescriptor | null) => {
    onChange(face);
    setOpen(false);
  };
  return (
    <div className="font-picker">
      <button
        type="button"
        className="button"
        aria-expanded={open}
        aria-label={label}
        data-font-id={value || ""}
        onClick={() => setOpen(!open)}
      >
        {value && fonts.get(value) ? (
          <FaceSample face={fonts.get(value)!} />
        ) : (
          "Use bundled fallback family"
        )}
      </button>
      {open && (
        <div role="group" aria-label="Choose a managed font">
          <input
            aria-label="Search fonts"
            placeholder="Search fonts…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
          />
          <button type="button" className="button" onClick={() => choose(null)}>
            Use bundled fallback family
          </button>
          <div style={{ display: "grid", maxHeight: "45vh", overflow: "auto" }}>
            {faces.slice(current * 12, current * 12 + 12).map((face) => (
              <button
                type="button"
                className="button"
                key={face.id}
                aria-pressed={value === face.id}
                data-font-id={face.id}
                onClick={() => choose(face)}
              >
                <FaceSample face={face} />
              </button>
            ))}
          </div>
          <div className="button-row">
            <button
              type="button"
              className="button"
              disabled={!current}
              onClick={() => setPage(current - 1)}
            >
              Previous fonts
            </button>
            <span>
              {faces.length} fonts · page {current + 1}
            </span>
            <button
              type="button"
              className="button"
              disabled={(current + 1) * 12 >= faces.length}
              onClick={() => setPage(current + 1)}
            >
              Next fonts
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
